import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useQuery } from '@tanstack/react-query'
import {
  ContinueFinancingDisableReason,
  SupplementalLoanEligibility,
  toContinueFinancingTranslationKeys,
} from '../types'

const SCOPE = 'supplementalLoanEligibility'
const DETAIL = 'detail'

export const supplementalLoanEligibilityKeys = {
  all: [SCOPE] as const,
  byApplicationId: (financingProgramId: EFinancingProgram, creditApplicationId: string) =>
    [SCOPE, DETAIL, financingProgramId, creditApplicationId] as const,
}

// GET OPERATION
const getSupplementalLoanEligibility = async (
  financingProgramId: EFinancingProgram,
  creditApplicationId: string,
) => {
  const apiClient = getApiClient()
  const url = `/Applications/${financingProgramId}/${creditApplicationId}/EligibleToSupplementalLoan`
  const response = await apiClient.get<SupplementalLoanEligibility>(url)
  return response.data
}

/**
 * Reasons the "Continue financing" button must stay disabled, as translation keys under
 * `continueFinancing`. Empty while loading, so the button is only disabled once the
 * server has actually refused.
 */
export function useContinueFinancingDisableReasons(
  financingProgramId: EFinancingProgram | undefined,
  creditApplicationId: string | undefined,
): [ContinueFinancingDisableReason[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: supplementalLoanEligibilityKeys.byApplicationId(
      financingProgramId ?? ('' as EFinancingProgram),
      creditApplicationId ?? '',
    ),
    queryFn: () => getSupplementalLoanEligibility(financingProgramId!, creditApplicationId!),
    enabled: !!financingProgramId && !!creditApplicationId,
  })

  return [toContinueFinancingTranslationKeys(data?.refusalReasonCodes ?? []), isFetching]
}
